"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { H4, H6 } from "../Common/Text";
import ProfileHeaderLayout from "../Profile/BasicInfo/ProfileHeaderLayout";
import Footer from "../Footer";
import StatItem from "../Common/StatItem";
import { Skeleton } from "@/components/ui/adapters";

function Placeholder({
  className,
  ...props
}: React.ComponentProps<typeof Skeleton>): React.ReactElement {
  return (
    <Skeleton
      className={`rounded-lg bg-secondary-light ${className ?? ""}`}
      {...props}
    />
  );
}

/** Data-free placeholders mirror the Profile header's responsive grid. */
export function ProfilePageSkeleton({
  showActions = false,
}: {
  showActions?: boolean;
}): React.ReactElement {
  const t = useTranslations();
  return (
    <div
      aria-label="Loading profile"
      role="status"
      className="w-full flex flex-col min-h-[calc(100dvh-4.5rem-env(safe-area-inset-bottom))] xl:min-h-screen"
    >
      <div className="contents" data-testid="profile-skeleton-mobile">
        <ProfileHeaderLayout
          loading
          cover={
            <Placeholder
              className="w-full bg-muted-light rounded-none xl:rounded-tr-2xl print:rounded-tr-2xl"
              data-testid="profile-skeleton-cover"
              style={{ aspectRatio: "3 / 1" }}
            />
          }
          avatar={
            <Placeholder
              className="rounded-full shrink-0 size-25 xl:size-30 print:size-30"
              data-testid="profile-skeleton-mobile-avatar"
            />
          }
          name={
            <div className="h-7.5 md:h-9 flex items-center xl:justify-center print:justify-center">
              <Placeholder className="h-5 w-24" />
            </div>
          }
          address={
            <div className="flex lg:justify-center lg:pb-4">
              <div
                className="flex h-9 items-center gap-2 rounded-3xl bg-white px-3"
                data-testid="profile-skeleton-share-address"
              >
                <Placeholder className="size-4.5 shrink-0 rounded-full" />
                <Placeholder className="h-4 w-20" />
                <Placeholder className="size-4.5 shrink-0" />
                <Placeholder className="size-4.5 shrink-0" />
              </div>
            </div>
          }
          status={
            <div className="pb-2 pt-4 lg:pb-4 lg:pt-0">
              <Placeholder className="h-5 w-56 max-w-full" />
            </div>
          }
          follow={
            <div className="flex w-full xl:justify-center items-center gap-4 xl:pb-4 pt-4 xl:pt-0">
              <Placeholder className="h-5 w-24" />
              <Placeholder className="h-5 w-24" />
            </div>
          }
          action={
            showActions ? (
              <div className="xl:px-4 print:px-4 w-full md:w-auto">
                <Placeholder
                  className="h-10 w-full min-w-60 rounded-xl"
                  data-testid="profile-skeleton-mobile-action"
                />
              </div>
            ) : null
          }
          ranking={
            <div className="w-full flex flex-col flex-1 justify-center items-center xl:min-h-28 print:min-h-28">
              <div className="w-full grid grid-cols-4 py-4 xl:py-0 shadow-md xl:shadow-none rounded-b-2xl xl:rounded-none print:shadow-none print:rounded-none">
                {Array.from({ length: 4 }, (_, index) => (
                  <StatItem
                    key={index}
                    label={
                      <span className="flex items-center justify-center h-5">
                        <span className="animate-pulse rounded-lg bg-secondary-light block h-3 w-14" />
                      </span>
                    }
                  >
                    <Placeholder className="h-7 w-12 md:h-8 md:w-14" />
                  </StatItem>
                ))}
              </div>
            </div>
          }
        />
        <div
          className="lg:hidden print:hidden mt-6"
          data-testid="profile-skeleton-gallery"
        >
          <div
            className="flex justify-center gap-9 mb-6"
            data-testid="profile-skeleton-gallery-tabs"
          >
            <div className="border-b-2 border-primary pb-1">
              <H6>{t("nft_gallery")}</H6>
            </div>
          </div>
          {/* Keep space for real NFTs without flashing a guessed card count. */}
          <div className="min-h-27 md:min-h-39.5" />
        </div>
      </div>
      <div
        data-testid="profile-skeleton-gallery-desktop"
        className="hidden lg:block print:block mx-auto w-full my-8 px-8 lg:max-w-(--breakpoint-lg) xl:max-w-(--breakpoint-xl) print:max-w-(--breakpoint-lg)"
      >
        <div className="flex items-center min-h-11.5 mb-6">
          <H4>{t("nft_gallery")}</H4>
        </div>
        <div className="min-h-54 xl:min-h-70" />
      </div>
      <Footer />
    </div>
  );
}
